import React from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Footer } from "../components/footer";
import CropPhenologyTimeline from "../components/crops/CropPhenologyTimeline";
import CropStoryCard from "../components/crops/CropStoryCard";
import { cropText } from "../components/crops/cropText";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { buildPlatformSignUpUrl } from "../utils/publicFarmPreview";
import cropsData from "../data/crops.json";

/**
 * /crops/:slug — crop story page. Marketing surface; the marquee element is
 * the phenology timeline (the proof object for the "Egyptian crop models"
 * positioning). Cross-links to the public crop wiki at /wiki/:slug.
 */
export default function CropStory({ lang = "EN", toggleLanguage }) {
  const isAr = lang === "AR";
  const { slug } = useParams();
  const crop = cropsData.crops.find((c) => c.slug === slug);

  useDocumentMeta({
    title: crop
      ? `${cropText(crop.story.headline, lang)} — FarmTopia`
      : "FarmTopia",
    description: crop ? cropText(crop.story.lead, lang).slice(0, 160) : undefined,
  });

  if (!crop) {
    return (
      <div dir={isAr ? "rtl" : "ltr"} className="bg-gray-50 min-h-screen">
        <Navbar lang={lang} toggleLanguage={toggleLanguage} />
        <main className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {isAr ? "لم نجد هذا المحصول" : "Crop not found"}
          </h1>
          <Link to="/crops" className="mt-4 inline-block text-emerald-700 font-medium">
            {isAr ? "تصفّح كل المحاصيل ←" : "← Browse all crops"}
          </Link>
        </main>
        <Footer lang={lang} />
      </div>
    );
  }

  const siblings = cropsData.crops.filter((c) => c.slug !== crop.slug);

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="bg-gray-50 min-h-screen">
      <Navbar lang={lang} toggleLanguage={toggleLanguage} />
      <main>
        {/* Hero */}
        <section className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <Link to="/crops" className="text-sm text-emerald-700">
                {isAr ? "المحاصيل" : "Crops"}
              </Link>
              <h1 className="mt-2 text-3xl md:text-4xl font-bold text-gray-900">
                {cropText(crop.story.headline, lang)}
              </h1>
              <p className="text-sm text-gray-500 italic mt-1">{crop.scientificName}</p>
              <p className="mt-4 text-gray-700 leading-relaxed">
                {cropText(crop.story.lead, lang)}
              </p>
            </div>
            <div className="h-56 md:h-64 rounded-xl bg-emerald-50 overflow-hidden flex items-center justify-center">
              <img
                src={crop.image}
                alt={cropText(crop.name, lang)}
                className="object-cover w-full h-full"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>
        </section>

        {/* Facts strip */}
        <section className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              [isAr ? "الموسم" : "Season", crop.facts.season],
              [isAr ? "احتياج المياه" : "Water need", crop.facts.waterNeed],
              [isAr ? "كثافة الزراعة" : "Plant density", crop.facts.plantDensity],
              [isAr ? "الأصناف الشائعة" : "Common varieties", crop.facts.varieties],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
                <div className="mt-1 text-sm font-medium text-gray-800">
                  {cropText(value, lang)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Phenology timeline — marquee element */}
        <section className="max-w-6xl mx-auto px-4 py-8">
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-xl font-bold text-gray-900">
                {isAr ? "مراحل النمو ونموذج المحصول" : "Growth stages & crop model"}
              </h2>
              <Link
                to={`/wiki/${crop.slug}`}
                className="text-sm font-medium text-emerald-700 hover:underline"
              >
                {isAr ? "اطّلع على كامل علم المحصول ←" : "See the full crop science →"}
              </Link>
            </div>
            <p className="mt-1 mb-5 text-sm text-gray-600">
              {crop.cycleType === "perennial"
                ? isAr
                  ? "الدورة السنوية لهذا المحصول المعمّر."
                  : "The annual cycle of this perennial crop."
                : isAr
                ? `دورة المحصول الكاملة عبر ${crop.totalDays} يومًا من الزراعة.`
                : `The full crop cycle across ${crop.totalDays} days from planting.`}
            </p>
            <CropPhenologyTimeline crop={crop} lang={lang} />
          </div>
        </section>

        {/* Where FarmTopia checks your crop */}
        <section className="max-w-6xl mx-auto px-4 py-8">
          <h2 className="text-xl font-bold text-gray-900">
            {isAr ? "أين تتحقق فارمتوبيا من محصولك" : "Where FarmTopia checks your crop"}
          </h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {crop.farmtopiaForCrop.map((item, i) => (
              <div key={i} className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-emerald-700">
                  {cropText(item.title, lang)}
                </h3>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                  {cropText(item.text, lang)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing snippet */}
        <section className="max-w-6xl mx-auto px-4 py-8">
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isAr ? `ابدأ إدارة ${cropText(crop.name, lang)} مع فارمتوبيا` : `Start managing ${cropText(crop.name, lang)} with FarmTopia`}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isAr
                  ? "خطة مجانية للبدء، وخطط مدفوعة بالجنيه المصري حسب حجم مزرعتك."
                  : "A free plan to start, paid plans in EGP that scale with your farm."}
              </p>
            </div>
            <Link
              to="/platform#pricing-plan"
              className="rounded-md bg-white border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              {isAr ? "اطّلع على الأسعار" : "See pricing"}
            </Link>
          </div>
        </section>

        {/* CTA — placeholder until <RoleRoutedCTA> (PRD-04) lands */}
        <section className="max-w-6xl mx-auto px-4 py-8">
          <div className="rounded-xl bg-gray-900 text-white p-8 text-center">
            <h2 className="text-2xl font-bold">
              {isAr ? `جرّب فارمتوبيا على ${cropText(crop.name, lang)}` : `Try FarmTopia on ${cropText(crop.name, lang)}`}
            </h2>
            <p className="mt-2 text-gray-300">
              {isAr ? "أنشئ حسابك وابدأ بحقلك الأول." : "Create your account and start with your first field."}
            </p>
            <a
              href={buildPlatformSignUpUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-block rounded-md bg-emerald-500 px-6 py-2.5 font-semibold text-white hover:bg-emerald-600"
            >
              {isAr ? "ابدأ مجانًا" : "Start for free"}
            </a>
          </div>
        </section>

        {/* Sibling crops */}
        <section className="max-w-6xl mx-auto px-4 py-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {isAr ? "محاصيل أخرى" : "Other crops"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {siblings.map((c) => (
              <CropStoryCard key={c.slug} crop={c} lang={lang} />
            ))}
          </div>
        </section>
      </main>
      <Footer lang={lang} />
    </div>
  );
}
